import { getSchema, type JSONContent, type Editor, type Schema } from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Highlight from "@tiptap/extension-highlight";
import History from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "src/lib/cn";
import { useNearViewport } from "src/hooks/useNearViewport";
import { useTiptapFieldSearchable, useSearchableKeyboardShortcut, useSearchableRegistry } from "src/lib/searchable";
import { TiptapErrorBoundary } from "src/components/TiptapErrorBoundary";
import { Container } from "./styles";
import { TrackChangeExtension } from "../../../../src/shared/trackChanges";
import { cleanTiptapDiffs, tipTapDocJsonHasTrackMarks } from "../../utils/diffToTiptap";
import {
  STORY_BIBLE_ACCEPT_TIPTAP_DIFFS_EVENT,
  type StoryBibleAcceptTiptapDiffsDetail,
} from "../../utils/storyBibleAcceptTiptapDiffs";
import { PasteHandler } from "./paste";
import { stripUnknownMarks } from "../../../../src/shared/schemas/stripUnknownMarks";
import { convertToParagraphs } from "./convertToParagraphs";

/** Lazily built so importing TiptapField does not call getSchema at module load (vitest mocks @tiptap/core). */
let tiptapFieldContentSchema: Schema | null = null;

const getTiptapFieldContentSchema = (): Schema => {
  if (!tiptapFieldContentSchema) {
    tiptapFieldContentSchema = getSchema([
      Document,
      Paragraph,
      History,
      Highlight.configure({ multicolor: true }),
      Text,
      Bold,
      Italic,
      Underline,
      TextStyle,
      HardBreak,
      TrackChangeExtension.configure({ enabled: false }),
      BulletList,
      OrderedList,
      ListItem,
    ]);
  }
  return tiptapFieldContentSchema;
};

const sanitizeTiptapFieldContent = (content: string | JSONContent | null | undefined): JSONContent | null => {
  const converted = convertToParagraphs(content as string | JSONContent);
  if (!converted) return null;
  return stripUnknownMarks(converted, getTiptapFieldContentSchema()) as JSONContent;
};

interface TiptapFieldProps {
  onChange: (json: JSONContent) => void;
  value: string | JSONContent | null;
  className?: string;
  placeholder?: string;
  readOnly?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  loading?: boolean;
  searchableId?: string;
  searchableName?: string;
  userId?: string;
  projectId?: string;
  ariaLabel?: string;
  fieldLimitExceeded?: boolean;
}

/**
 * Render a paragraph's inline content (text, hardBreak) as React nodes.
 */
const renderInline = (nodes: JSONContent[] | undefined): ReactNode[] => {
  if (!nodes) return [];
  const out: ReactNode[] = [];
  nodes.forEach((n, i) => {
    if (n.type === "text" && n.text) out.push(n.text);
    else if (n.type === "hardBreak") out.push(<br key={i} />);
  });
  return out;
};

/**
 * Does this node (or any descendant) contain any rendered text?
 */
const nodeHasText = (node: JSONContent): boolean => {
  if (node.type === "text") return !!node.text;
  return node.content?.some(nodeHasText) ?? false;
};

/**
 * Render a single block-level TipTap node to match ProseMirror's DOM output,
 * so the placeholder's height matches the real editor's height.
 */
const renderBlockNode = (node: JSONContent, key: number | string): ReactNode => {
  if (node.type === "paragraph") {
    const inline = renderInline(node.content);
    return <p key={key}>{inline.length > 0 ? inline : <br />}</p>;
  }
  if (node.type === "bulletList") {
    return <ul key={key}>{node.content?.map((child, i) => renderBlockNode(child, i))}</ul>;
  }
  if (node.type === "orderedList") {
    return <ol key={key}>{node.content?.map((child, i) => renderBlockNode(child, i))}</ol>;
  }
  if (node.type === "listItem") {
    return <li key={key}>{node.content?.map((child, i) => renderBlockNode(child, i))}</li>;
  }
  return null;
};

/**
 * Render TipTap JSON as static DOM matching ProseMirror's output.
 * Handles paragraphs, bullet/ordered lists, and hard breaks so heights match.
 */
const renderStaticContent = (value: JSONContent | string | null | undefined, placeholder?: string) => {
  if (!value || typeof value === "string") {
    const text = typeof value === "string" ? value : "";
    if (!text) {
      return <p className="is-editor-empty" style={{ color: "var(--grey-2)" }}>{placeholder}</p>;
    }
    return <p>{text}</p>;
  }

  const topLevel = value.content;
  if (!topLevel || topLevel.length === 0) {
    return <p className="is-editor-empty" style={{ color: "var(--grey-2)" }}>{placeholder}</p>;
  }

  if (topLevel.every((n) => !nodeHasText(n))) {
    return <p className="is-editor-empty" style={{ color: "var(--grey-2)" }}>{placeholder}</p>;
  }

  return topLevel.map((n, i) => renderBlockNode(n, i));
};

/**
 * Subscribes to global search state and returns true while a search term is active.
 * Setter is a no-op when the boolean doesn't change, so 2000+ consumers only
 * re-render on the active→inactive (or inverse) transition, not on every keystroke.
 */
const useIsSearchActive = (): boolean => {
  const { subscribeToSearchState, getSearchState } = useSearchableRegistry();
  const [active, setActive] = useState(() => !!getSearchState().searchTerm);
  useEffect(() => {
    return subscribeToSearchState((state) => {
      const next = !!state.searchTerm;
      setActive((prev) => (prev === next ? prev : next));
    });
  }, [subscribeToSearchState]);
  return active;
};

/**
 * Static placeholder that mimics TiptapField's exact DOM structure
 * without mounting a ProseMirror EditorView. Renders <p> elements
 * inside a .ProseMirror div to match the real editor's layout.
 */
const TiptapFieldPlaceholder = ({
  value,
  className,
  placeholder,
  loading,
  fieldLimitExceeded,
}: Pick<TiptapFieldProps, "value" | "className" | "placeholder" | "loading" | "fieldLimitExceeded">) => (
  <Container className={cn("tip-tap-field-container transition-all", loading && "opacity-50", fieldLimitExceeded && "field-limit-exceeded", className && { [className]: true })}>
    <div>
      <div className="ProseMirror" data-placeholder-field="true">
        {renderStaticContent(value as JSONContent, placeholder)}
      </div>
    </div>
  </Container>
);

export const RestorableTiptapField = ({ restoring, searchableId, searchableName, ariaLabel, ...rest }: TiptapFieldProps & { restoring?: boolean }) => {
  const [ref, isNearViewport] = useNearViewport();
  // While a global Find/Replace search is active, mount the real editor so the
  // field registers with the searchable registry — otherwise placeholder-only
  // fields would be invisible to the app's Find/Replace.
  const isSearchActive = useIsSearchActive();

  if (restoring) {
    return null;
  }

  const shouldMountEditor = isNearViewport || isSearchActive;

  return (
    <div ref={ref}>
      {shouldMountEditor ? (
        <TiptapField {...rest} searchableId={searchableId} searchableName={searchableName} ariaLabel={ariaLabel} />
      ) : (
        <TiptapFieldPlaceholder
          value={rest.value}
          className={rest.className}
          placeholder={rest.placeholder}
          loading={rest.loading}
          fieldLimitExceeded={rest.fieldLimitExceeded}
        />
      )}
    </div>
  );
};

export default function TiptapField({
  onChange,
  value,
  className,
  placeholder,
  readOnly,
  onFocus,
  onBlur,
  loading,
  // Global search props
  searchableId,
  searchableName,
  // Error tracking props
  userId,
  projectId,
  // Accessibility
  ariaLabel,
  fieldLimitExceeded,
}: TiptapFieldProps) {
  // these are no-ops, but we need to provide a function to the editor to prevent TypeErrors
  const handleFocus = onFocus || (() => {});
  const handleBlur = onBlur || (() => {});
  const hasDiffsRef = useRef(false);
  const acceptingChangesRef = useRef(false);
  const acceptDiffsRef = useRef<(editorInstance: Editor) => void>(() => {});

  const acceptDiffs = (editorInstance: Editor) => {
    if (!editorInstance) return;
    acceptingChangesRef.current = true;
    try {
      const cleaned = cleanTiptapDiffs(editorInstance.getJSON());
      const { state, view } = editorInstance;
      const doc = state.schema.nodeFromJSON(cleaned);
      const tr = state.tr.replaceWith(0, state.doc.content.size, doc);
      tr.setMeta("trackManualChanged", true);
      view.dispatch(tr);
      hasDiffsRef.current = false;
    } catch (error) {
      console.error("Failed to accept diffs", error);
    } finally {
      acceptingChangesRef.current = false;
    }
  };
  acceptDiffsRef.current = acceptDiffs;

  // Track the last content we set/received to avoid reverting programmatic changes
  const lastKnownContentRef = useRef<string | null>(null);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      History,
      Highlight.configure({
        multicolor: true,
      }),
      Text,
      Bold,
      Italic,
      Underline,
      TextStyle,
      HardBreak,
      Placeholder.configure({ placeholder }),
      TrackChangeExtension.configure({ enabled: false }),
      PasteHandler,
      BulletList,
      OrderedList,
      ListItem,
    ],
    editorProps: {
      attributes: {
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
    content: sanitizeTiptapFieldContent(value) ?? undefined,
    editable: !readOnly,
    onUpdate: ({ editor, transaction }) => {
      if (readOnly) return;

      // Skip onChange for programmatic updates (from useEffect syncing external value)
      // This prevents infinite loops when diff content is loaded
      if (transaction.getMeta("trackManualChanged")) {
        return;
      }

      // If we have diffs loaded and user is typing, accept all changes first
      if (
        hasDiffsRef.current &&
        !acceptingChangesRef.current &&
        editor.commands.hasTrackedChanges()
      ) {
        acceptDiffs(editor);
      }

      const json = editor.getJSON();
      // Track the content we're sending up so we don't revert it
      lastKnownContentRef.current = JSON.stringify(json);
      onChange(json);
    },
    onBlur: handleBlur,
    onFocus: ({ editor }) => {
      if (hasDiffsRef.current && (editor as any).commands.hasTrackedChanges()) {
        acceptDiffs(editor);
      }
      handleFocus();
    },
  });

  // Register with global search if searchableId is provided
  useTiptapFieldSearchable(editor, {
    id: searchableId,
    name: searchableName,
  });

  // Accept All in agentic chat: strip track marks on dirty Story Bible fields only.
  useEffect(() => {
    if (!searchableId) return;

    const handleAcceptStoryBibleDiffs = (event: Event) => {
      const detail = (event as CustomEvent<StoryBibleAcceptTiptapDiffsDetail>).detail;
      if (!detail?.searchableIds?.includes(searchableId)) return;

      const editorInstance = editor;
      if (!editorInstance || editorInstance.isDestroyed) return;

      const editorTracked = !!(editorInstance as any).commands?.hasTrackedChanges?.();
      const editorJsonHasTracks = tipTapDocJsonHasTrackMarks(editorInstance.getJSON());
      if (!editorTracked && !editorJsonHasTracks) return;

      acceptDiffsRef.current(editorInstance);
      lastKnownContentRef.current = JSON.stringify(editorInstance.getJSON());
    };

    window.addEventListener(STORY_BIBLE_ACCEPT_TIPTAP_DIFFS_EVENT, handleAcceptStoryBibleDiffs);
    return () => {
      window.removeEventListener(STORY_BIBLE_ACCEPT_TIPTAP_DIFFS_EVENT, handleAcceptStoryBibleDiffs);
    };
  }, [editor, searchableId]);

  // Enable Ctrl/Cmd+F to open global find/replace
  const containerRef = useRef<HTMLDivElement>(null);
  useSearchableKeyboardShortcut(containerRef);

  useEffect(() => {
    if (editor && !editor.isFocused) {
      editor.setEditable(!readOnly && !loading);
    }
  }, [loading, readOnly]);

  // Visibility change listener only when field is focused
  // This flushes pending saves when user switches tabs while editing
  useEffect(() => {
    if (!editor) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && editor.isFocused) {
        handleBlur();
      }
    };

    // Only add listener when focused
    if (editor.isFocused) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    }
  }, [editor, editor?.isFocused, handleBlur]);

  // Sync external value into the editor when it changes.
  // Handles both regular content updates and tracked changes (diffs).
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    // Never clear a focused editor just because `value` is transiently falsy (re-fetch, parent reset).
    if (!value) {
      if (editor.isFocused) {
        return;
      }
      editor.commands.clearContent(true);
      return;
    }

    const convertedContent = sanitizeTiptapFieldContent(value);
    if (!convertedContent) return;

    const currentJSON = editor.getJSON();
    const nextJSON = convertedContent;
    const nextJSONString = JSON.stringify(nextJSON);

    if (lastKnownContentRef.current === nextJSONString) {
      return;
    }

    // Preserve unsynced edits while focused — unless Story Bible/chat cleared AI diff marks externally
    // (same field still focused) while the incoming prop is already plain content.
    const editorTracked = !!(editor as any).commands?.hasTrackedChanges?.();
    const incomingTracked = tipTapDocJsonHasTrackMarks(nextJSON);
    if (editor.isFocused && !(editorTracked && !incomingTracked)) {
      return;
    }

    if (JSON.stringify(currentJSON) !== nextJSONString) {
      const { state, view } = editor;
      const { schema } = state;
      try {
        const doc = schema.nodeFromJSON(nextJSON);
        const tr = state.tr.replaceWith(0, state.doc.content.size, doc);
        tr.setMeta("trackManualChanged", true);
        view.dispatch(tr);

        // Check if the newly loaded content has tracked changes
        if ((editor as any).commands.hasTrackedChanges()) {
          hasDiffsRef.current = true;
        } else {
          hasDiffsRef.current = false;
        }
      } catch (e) {
        console.error("Error updating Tiptap content", e);
        editor.commands.setContent(nextJSON, false);
      }
    }
  }, [editor, value]);

  return (
    <TiptapErrorBoundary editorName="TiptapField" documentId={searchableId} userId={userId} projectId={projectId}>
      <Container ref={containerRef} className={cn("tip-tap-field-container transition-all", loading && !editor?.isFocused && "opacity-50", fieldLimitExceeded && "field-limit-exceeded", { [className!]: className })}>
        <EditorContent editor={editor} />
      </Container>
    </TiptapErrorBoundary>
  );
}
